/** Layout save/restore dropdown — lives in the tab bar area. */

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useLayoutStore } from "@/lib/workspace-layouts";
import { useWorkspaceStore } from "@/lib/workspace";
import { Layout, Save, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export function LayoutMenu() {
	const { t } = useTranslation();
	const wsId = useWorkspaceStore((s) => s.selectedId);
	const { getLayouts, saveLayout, restoreLayout, deleteLayout } =
		useLayoutStore();
	const [showSaveForm, setShowSaveForm] = useState(false);
	const [saveName, setSaveName] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	if (!wsId) return null;

	const layouts = getLayouts(wsId);

	const handleSave = () => {
		if (!saveName.trim()) return;
		saveLayout(wsId, saveName.trim());
		setSaveName("");
		setShowSaveForm(false);
	};

	return (
		<div className="flex items-center shrink-0">
			<DropdownMenu
				onOpenChange={(open) => {
					if (!open) setShowSaveForm(false);
				}}
			>
				<DropdownMenuTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="w-7 h-7"
						title={t("layouts")}
						aria-label={t("layouts")}
					>
						<Layout className="h-3.5 w-3.5" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-52">
					{layouts.length > 0 && (
						<>
							{layouts.map((layout) => (
								<DropdownMenuItem
									key={layout.id}
									className="flex items-center justify-between group"
									onClick={() => restoreLayout(wsId, layout.id)}
								>
									<span className="truncate text-xs">{layout.name}</span>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="opacity-0 group-hover:opacity-100 h-5 w-5"
										onClick={(e) => {
											e.stopPropagation();
											deleteLayout(wsId, layout.id);
										}}
										aria-label={t("delete")}
										title={t("delete")}
									>
										<Trash2 className="h-3 w-3 text-muted-foreground" />
									</Button>
								</DropdownMenuItem>
							))}
							<DropdownMenuSeparator />
						</>
					)}
					{showSaveForm ? (
						<div
							className="flex gap-1.5 p-1.5"
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
						>
							<Input
								ref={inputRef}
								value={saveName}
								onChange={(e) => setSaveName(e.target.value)}
								placeholder={t("layout_name_placeholder")}
								className="h-7 text-xs"
								autoFocus
								onKeyDown={(e) => {
									if (e.key === "Enter") handleSave();
									if (e.key === "Escape") setShowSaveForm(false);
								}}
							/>
							<Button
								size="sm"
								className="h-7 px-2 text-xs shrink-0"
								onClick={handleSave}
								disabled={!saveName.trim()}
							>
								{t("save")}
							</Button>
						</div>
					) : (
						<DropdownMenuItem
							onSelect={(e) => {
								e.preventDefault();
								setShowSaveForm(true);
							}}
						>
							<Save className="h-3.5 w-3.5 mr-2" />
							<span className="text-xs">{t("save_layout")}</span>
						</DropdownMenuItem>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
